import React from 'react';
import './AboutPage.css';
import { Typography, Row, Col, Space, Tag, Card, Divider } from 'antd';
import { HeartOutlined, GiftOutlined, TruckOutlined, SmileOutlined, StarOutlined, EnvironmentOutlined, ClockCircleOutlined, PhoneOutlined, MessageOutlined } from '@ant-design/icons';
import { Breadcrumbs, getPageBreadcrumbs } from '../../shared/ui/Breadcrumbs';
import { useSEO } from '../../shared/ui/SEOHead';
import { SHOP_INFO, ADVANTAGES, SERVICES } from '../../shared/constants/shopData';

const { Title, Text, Paragraph } = Typography;

const renderAdvIcon = (iconName: string) => {
    switch (iconName) {
        case 'HeartOutlined': return <HeartOutlined style={{ fontSize: 32, color: '#e91e63' }} />;
        case 'GiftOutlined': return <GiftOutlined style={{ fontSize: 32, color: '#e91e63' }} />;
        case 'TruckOutlined': return <TruckOutlined style={{ fontSize: 32, color: '#e91e63' }} />;
        case 'SmileOutlined': return <SmileOutlined style={{ fontSize: 32, color: '#e91e63' }} />;
        case 'StarOutlined': return <StarOutlined style={{ fontSize: 32, color: '#e91e63' }} />;
        case 'EnvironmentOutlined': return <EnvironmentOutlined style={{ fontSize: 32, color: '#e91e63' }} />;
        default: return <HeartOutlined style={{ fontSize: 32, color: '#e91e63' }} />;
    }
};

export const AboutPage: React.FC = () => {
    useSEO({
        title: "О компании — цветочный магазин «Ваниль» в Булгаково и Уфе",
        description: "История цветочной мастерской «Ваниль», наши ценности, магазины в Булгаково и Уфе, профессиональные флористы и гарантия качества.",
        canonicalUrl: "https://vanilufa.ru/about"
    });

    const breadcrumbs = getPageBreadcrumbs('/about');

    return (
        <div className="about-page">
            <Breadcrumbs items={breadcrumbs} className="about-page__breadcrumbs" />
            <div className="about-page__hero">
                <Title level={1} className="about-page__title">О цветочном магазине «Ваниль»</Title>
                <Text type="secondary" className="about-page__subtitle">
                    Дарим красоту и эмоции каждый день с 2021 года
                </Text>
            </div>

            <Divider className="about-page__divider" />

            <section className="about-page__section">
                <Title level={2} className="about-page__section-title">Наша история</Title>
                <Text className="about-page__text">
                    «Ваниль» началась с открытия уютного цветочного магазина в селе Булгаково Уфимского района. Основатели — семья ценителей красоты,
                    которые хотели радовать жителей по-настоящему свежими, красивыми и стойкими цветами.
                </Text>
                <Text className="about-page__text">
                    За годы работы мы выросли в полноценную цветочную студию с профессиональной командой флористов, регулярными поставками свежих цветов,
                    двумя магазинами (в Булгаково и в Уфе) и бережной доставкой по всему Уфимскому району и городу Уфа.
                </Text>
            </section>

            <Divider className="about-page__divider" />

            <section className="about-page__section">
                <Title level={2} className="about-page__section-title">Почему выбирают нас</Title>
                <Row gutter={[24, 24]} className="about-page__advantages">
                    {ADVANTAGES.map((adv) => (
                        <Col key={adv.id} xs={24} md={12} lg={8}>
                            <Card className="about-page__advantage-card" variant="borderless">
                                <Space orientation="vertical" size="middle">
                                    {renderAdvIcon(adv.icon)}
                                    <Title level={4}>{adv.title}</Title>
                                    <Text type="secondary">{adv.desc}</Text>
                                </Space>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </section>

            <Divider className="about-page__divider" />

            <section className="about-page__section">
                <Title level={2} className="about-page__section-title">Наши услуги</Title>
                <div className="about-page__services-list" style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                    {SERVICES.map((item, idx) => (
                        <Tag key={idx} color="pink" icon={<GiftOutlined />} style={{ padding: '6px 14px', fontSize: '14px', borderRadius: 16 }}>
                            {item.title}
                        </Tag>
                    ))}
                </div>
            </section>

            <Divider className="about-page__divider" />

            <section className="about-page__cta">
                <Card className="about-page__cta-card" variant="borderless" style={{ background: 'linear-gradient(135deg, #fff0f5 0%, #ffe4ec 100%)' }}>
                    <Space orientation="vertical" size="large" style={{ textAlign: 'center', width: '100%' }}>
                        <Title level={2}>Хотите заказать букет или нужна консультация?</Title>
                        <Text type="secondary" style={{ fontSize: 16 }}>
                            Звоните, пишите в Telegram или заказывайте прямо на сайте — подберём идеальные цветы для вашего случая
                        </Text>
                        <Space size="middle" wrap>
                            <a href={`tel:${SHOP_INFO.phoneClean}`} className="about-page__cta-button">
                                <PhoneOutlined /> Позвонить: +7 (987) 258-81-11
                            </a>
                            <a href={SHOP_INFO.socialMedia.telegram} target="_blank" rel="noopener noreferrer" className="about-page__cta-button about-page__cta-button--secondary">
                                <MessageOutlined /> Написать в Telegram
                            </a>
                            <a href={SHOP_INFO.socialMedia.max} target="_blank" rel="noopener noreferrer" className="about-page__cta-button about-page__cta-button--outline">
                                <MessageOutlined /> Написать в MAX
                            </a>
                        </Space>
                    </Space>
                </Card>
            </section>

            <section className="about-page__addresses">
                <Title level={2} className="about-page__section-title">Наши адреса</Title>
                <Row gutter={[24, 24]}>
                    <Col xs={24} md={12}>
                        <Card className="about-page__address-card" variant="borderless">
                            <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
                                <Space>
                                    <EnvironmentOutlined style={{ color: '#e91e63', fontSize: 20 }} />
                                    <Title level={4} style={{ margin: 0 }}>Булгаково</Title>
                                </Space>
                                <Text strong>{SHOP_INFO.addresses.bulgakovo}</Text>
                                <Space>
                                    <ClockCircleOutlined />
                                    <Text>Ежедневно: {SHOP_INFO.workingHours.bulgakovo}</Text>
                                </Space>
                                <Space wrap style={{ marginTop: 8 }}>
                                    <a
                                        href={`tel:${SHOP_INFO.phoneRaw}`}
                                        className="about-page__card-btn about-page__card-btn--call"
                                    >
                                        <PhoneOutlined /> Позвонить
                                    </a>
                                    <a
                                        href={`https://yandex.ru/maps/?text=${encodeURIComponent(SHOP_INFO.addresses.bulgakovo)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="about-page__card-btn about-page__card-btn--map"
                                    >
                                        <EnvironmentOutlined /> Точка на карте
                                    </a>
                                </Space>
                            </Space>
                        </Card>
                    </Col>
                    <Col xs={24} md={12}>
                        <Card className="about-page__address-card" variant="borderless">
                            <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
                                <Space>
                                    <EnvironmentOutlined style={{ color: '#e91e63', fontSize: 20 }} />
                                    <Title level={4} style={{ margin: 0 }}>Уфа</Title>
                                </Space>
                                <Text strong>{SHOP_INFO.addresses.ufa}</Text>
                                <Space>
                                    <ClockCircleOutlined />
                                    <Text>Ежедневно: {SHOP_INFO.workingHours.ufa}</Text>
                                </Space>
                                <Space wrap style={{ marginTop: 8 }}>
                                    <a
                                        href={`tel:${SHOP_INFO.phoneRaw}`}
                                        className="about-page__card-btn about-page__card-btn--call"
                                    >
                                        <PhoneOutlined /> Позвонить
                                    </a>
                                    <a
                                        href={`https://yandex.ru/maps/?text=${encodeURIComponent(SHOP_INFO.addresses.ufa)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="about-page__card-btn about-page__card-btn--map"
                                    >
                                        <EnvironmentOutlined /> Точка на карте
                                    </a>
                                </Space>
                            </Space>
                        </Card>
                    </Col>
                </Row>
            </section>
        </div>
    );
};